import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Wrench, Plus, Loader2, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface RepairOrder {
    ro_id: number;
    status: string;
    vehicle_vin: string | null;
    vehicle_desc: string | null;
    labor_total: string | null;
    parts_total: string | null;
    total_amount: string | null;
    created_at: string;
    appointment: { customer_name: string | null; customer_phone: string | null; service_type: string | null } | null;
}

const STATUSES = ["ALL", "OPEN", "IN_PROGRESS", "INVOICED", "PAID"];
const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

export default function ServiceRepairOrdersPage() {
    const [orders,  setOrders]  = useState<RepairOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState("ALL");
    const [search,  setSearch]  = useState("");

    useEffect(() => {
        setLoading(true);
        const params = filter !== "ALL" ? `?status=${filter}` : "";
        axios.get(`${API_URL}/repair-shops/staff/repair-orders${params}`, { withCredentials: true })
            .then((r) => setOrders(r.data.repair_orders ?? []))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, [filter]);

    const filtered = search
        ? orders.filter((o) =>
            o.vehicle_desc?.toLowerCase().includes(search.toLowerCase()) ||
            o.vehicle_vin?.toLowerCase().includes(search.toLowerCase()) ||
            o.appointment?.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
            String(o.ro_id).includes(search)
          )
        : orders;

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Repair Orders</h1>
                    <p className="text-muted-foreground text-sm mt-1">{filtered.length} repair order{filtered.length !== 1 ? "s" : ""}</p>
                </div>
                <Link to="/service/repair-orders/new">
                    <Button className="bg-indigo-600 text-white hover:bg-indigo-700">
                        <Plus className="h-4 w-4 mr-2" />New RO
                    </Button>
                </Link>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by customer, vehicle, or RO #"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <div className="flex gap-1">
                    {STATUSES.map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                filter === s ? "bg-indigo-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                            }`}
                        >
                            {s === "ALL" ? "All" : s.replace("_", " ")}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : filtered.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <Wrench className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No repair orders found</p>
                </Card>
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b">
                                <tr className="text-left">
                                    <th className="p-4 font-medium">RO #</th>
                                    <th className="p-4 font-medium">Customer</th>
                                    <th className="p-4 font-medium">Vehicle</th>
                                    <th className="p-4 font-medium">Service</th>
                                    <th className="p-4 font-medium">Status</th>
                                    <th className="p-4 font-medium text-right">Total</th>
                                    <th className="p-4 font-medium">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((ro) => (
                                    <tr key={ro.ro_id} className="border-b last:border-0 hover:bg-muted/30">
                                        <td className="p-4">
                                            <Link to={`/service/repair-orders/${ro.ro_id}`} className="font-medium text-indigo-600 hover:underline">
                                                #{ro.ro_id}
                                            </Link>
                                        </td>
                                        <td className="p-4">{ro.appointment?.customer_name ?? "—"}</td>
                                        <td className="p-4">
                                            <p>{ro.vehicle_desc ?? "—"}</p>
                                            {ro.vehicle_vin && <p className="text-xs text-muted-foreground font-mono">{ro.vehicle_vin}</p>}
                                        </td>
                                        <td className="p-4 text-muted-foreground">{ro.appointment?.service_type ?? "—"}</td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                {ro.status.replace("_", " ")}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right font-medium">
                                            {ro.total_amount ? `$${ro.total_amount}` : "—"}
                                        </td>
                                        <td className="p-4 text-muted-foreground text-xs">
                                            {new Date(ro.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
